import axios from 'axios';
import { User } from './auth.service';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface Tenant {
  id: string;
  name: string;
  email?: string;
  maxUsers: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  users?: User[];
}

export interface CreateTenantData {
  name: string;
  email?: string;
  maxUsers?: number;
  ownerEmail: string;
  ownerName: string;
  ownerPassword: string;
}

export interface UpdateTenantData {
  name?: string;
  email?: string;
  maxUsers?: number;
  isActive?: boolean;
}

export interface InviteUserData {
  email: string;
  name: string;
  password: string;
  /** Plattformrolle (user/admin); das Backend uebernimmt sie nur von Plattform-Admins. */
  role?: string;
  /** Mandantenrolle (member/owner). */
  tenantRole?: string;
  /** Anwendungsrolle (APP_ROLLEN); nur im Entwicklungsmodus ohne Keycloak wirksam. */
  appRolle?: string;
}

export interface UpdateUserRechteData {
  /** Rechte zusaetzlich zur Rolle (keine admin.*-Rechte). */
  zusatz: string[];
  /** Rechte der Rolle, die der Person entzogen sind. */
  entzug: string[];
}

export interface UpdateUserRoleData {
  role: string;
  tenantRole: string;
  /** Anwendungsrolle (APP_ROLLEN); nur im Entwicklungsmodus ohne Keycloak wirksam. */
  appRolle?: string;
}

class TenantService {
  /**
   * Get tenant by ID
   */
  async getTenant(tenantId: string): Promise<Tenant> {
    const response = await axios.get(`${API_URL}/tenants/${tenantId}`);
    return response.data.data;
  }

  /**
   * Update tenant settings
   */
  async updateTenant(tenantId: string, data: UpdateTenantData): Promise<Tenant> {
    const response = await axios.put(`${API_URL}/tenants/${tenantId}`, data);
    return response.data.data;
  }

  /**
   * Alle Mandanten (nur Plattform-Admin, admin.mandant.alle).
   */
  async listTenants(perPage = 200): Promise<Tenant[]> {
    const response = await axios.get(`${API_URL}/tenants`, { params: { page: 1, perPage } });
    return response.data.data;
  }

  /**
   * Get all users in a tenant
   */
  async getTenantUsers(tenantId: string): Promise<User[]> {
    const response = await axios.get(`${API_URL}/tenants/${tenantId}/users`);
    return response.data.data;
  }

  /**
   * Invite a user to a tenant. Im Keycloak-Betrieb antwortet das Backend 403
   * (Konten werden in Keycloak angelegt); die Oberflaeche zeigt den Dialog
   * dann gar nicht erst (Verbesserungsplan, Paket 2 Schritt 6).
   */
  async inviteUser(tenantId: string, data: InviteUserData): Promise<User> {
    const response = await axios.post(`${API_URL}/tenants/${tenantId}/users`, data);
    return response.data.data;
  }

  /**
   * Remove a user from a tenant
   */
  async removeUser(tenantId: string, userId: string): Promise<void> {
    await axios.delete(`${API_URL}/tenants/${tenantId}/users/${userId}`);
  }

  /**
   * Update user role in tenant (role, tenantRole und appRolle). Im
   * Keycloak-Betrieb antwortet das Backend 403 (Rollen werden in Keycloak
   * vergeben); die Oberflaeche bietet den Dialog dann nicht an.
   */
  async updateUserRole(
    tenantId: string,
    userId: string,
    data: UpdateUserRoleData
  ): Promise<User> {
    const response = await axios.put(`${API_URL}/tenants/${tenantId}/users/${userId}/role`, data);
    return response.data.data;
  }

  /**
   * Rechte je Person setzen (Entscheidung 17): beide Listen ersetzen die
   * gespeicherten. Auch im Keycloak-Betrieb offen; die Antwort traegt die
   * wirksamen `rechte`.
   */
  async updateUserRechte(
    tenantId: string,
    userId: string,
    data: UpdateUserRechteData
  ): Promise<User> {
    const response = await axios.put(`${API_URL}/tenants/${tenantId}/users/${userId}/rechte`, data);
    return response.data.data;
  }
}

const tenantService = new TenantService();
export default tenantService;
